import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useIntl } from 'react-intl';
import { toast } from 'sonner';
import { Queue } from '@rossum/api-client/queues';
import { QueueRule } from '@rossum/api-client/schemas';
import { notify } from '../../toaster';
import { RULES_QUERY_KEY } from './rulesQueryKeys';
import { useDeleteRule } from './useDeleteRule';
import { useDuplicateRule } from './useDuplicateRule';
import { usePatchRule } from './usePatchRule';

type UnlinkRuleParams = {
  rule: QueueRule;
  queue: Queue;
};

export const hasUnlinkAction = (
  queue: Queue | undefined,
  rule: QueueRule
): queue is Queue => Boolean(queue && rule.queues.length > 1);

type UseUnlinkRuleCallbacks = {
  onSuccess?: (unlinkedRuleId: number) => void;
  onUndoSuccess?: (originalRuleId: number) => void;
};

export const useUnlinkRule = (callbacks?: UseUnlinkRuleCallbacks) => {
  const intl = useIntl();
  const queryClient = useQueryClient();
  const { mutateAsync: patchRule } = usePatchRule();
  const { mutateAsync: duplicateRule } = useDuplicateRule();
  const { mutateAsync: deleteRule } = useDeleteRule();

  return useMutation({
    mutationFn: async ({ rule, queue }: UnlinkRuleParams) => {
      if (!hasUnlinkAction(queue, rule)) {
        return Promise.reject(
          new Error(
            'Cannot unlink rule: rule must be linked to more than one queue'
          )
        );
      }

      const originalQueues = rule.queues;
      const updatedQueues = originalQueues.filter(q => q !== queue.url);

      // First, patch the original rule to remove current queue
      // Then create a duplicate with only the current queue
      try {
        await patchRule({
          patchedRuleId: rule.id,
          patchedRule: { queues: updatedQueues },
          skipInvalidation: true,
        });
        return await duplicateRule({ rule, queue, skipInvalidation: true });
      } catch (error) {
        // Rollback: restore original queues on the source rule
        patchRule({
          patchedRuleId: rule.id,
          patchedRule: { queues: originalQueues },
          skipInvalidation: true,
        });
        throw error;
      }
    },
    onSuccess: (unlinkedRule, { rule }) => {
      queryClient.invalidateQueries([RULES_QUERY_KEY]);
      callbacks?.onSuccess?.(unlinkedRule.id);

      const toastId = `undo-unlink-rule-${unlinkedRule.id}`;
      notify.default({
        title: intl.formatMessage({
          id: 'features.queueSettings.rules.list.unlink.notification.success',
        }),
        toastActions: [
          {
            label: intl.formatMessage({
              id: 'components.toaster.actions.undo',
            }),

            onClick: () => {
              toast.dismiss(toastId);
              // Undo: delete the new rule and restore original queues
              deleteRule({
                deletedRuleId: unlinkedRule.id,
                skipInvalidation: true,
              })
                .then(() =>
                  patchRule({
                    patchedRuleId: rule.id,
                    patchedRule: { queues: rule.queues },
                    skipInvalidation: true,
                  })
                )
                .then(() => {
                  queryClient.invalidateQueries([RULES_QUERY_KEY]);
                  callbacks?.onUndoSuccess?.(rule.id);
                })
                .catch(() =>
                  notify.error({
                    title: intl.formatMessage({
                      id: 'features.queueSettings.rules.list.unlink.undo.notification.failure',
                    }),
                  })
                );
            },
          },
        ],
        id: toastId,
      });
    },
    onError: () =>
      notify.error({
        title: intl.formatMessage({
          id: 'features.queueSettings.rules.list.unlink.notification.failure',
        }),
      }),
  });
};
